import {
  type ComponentPropsWithoutRef,
  forwardRef,
  isValidElement,
  type ReactElement,
} from "react"
import { type Logo, type Icon } from "./iconCore"

function isReactElement(value: unknown): value is ReactElement {
  return isValidElement(value)
}

export const featuredIconColors = [
  "default",
  "neutral",
  "error",
  "success",
  "warning",
  "purple",
  "orange",
  "teal",
  "magenta",
  "pink",
] as const

export type FeaturedIconColor = (typeof featuredIconColors)[number]

export const featuredIconVariants = [
  "light-circle",
  "solid-circle",
  "square",
] as const

export type FeaturedIconVariant = (typeof featuredIconVariants)[number]

export const featuredIconSizes = ["sm", "md", "lg"] as const

export type FeaturedIconSize = (typeof featuredIconSizes)[number]

export type FeaturedIconProps = ComponentPropsWithoutRef<"div"> & {
  /**
   * The icon or logo to display in the FeaturedIcon component. Note that this is provided
   * as a prop intentionally to restrict the range of sizes available that
   * can be used.
   *
   * Also allows a ReactElement to be passed, in case you want to use a custom image.
   * Be sure to use var(--icon-size) to determine the size of that image, though.
   */
  icon: Icon | Logo | ReactElement

  /**
   * Indicates the colour of the FeaturedIcon. Note that this only applies to the
   * light-circle and solid-circle variants.
   *
   * @default default
   */
  "data-color"?: FeaturedIconColor

  /**
   * Indicates the variant of the FeaturedIcon. Note that not all properties apply to
   * all variants of the FeaturedIcon. When using the square variant the color does not
   * apply.
   *
   * @default light-circle
   */
  "data-variant"?: FeaturedIconVariant

  /**
   * Indicates the size of the FeauredIcon. Note that this only applies to the square variant.
   *
   * @default md
   */
  "data-size"?: FeaturedIconSize
}

export const FeaturedIcon = forwardRef<HTMLDivElement, FeaturedIconProps>(
  // eslint-disable-next-line @typescript-eslint/no-shadow
  ({ className, icon: Icon, ...restProps }, ref) => {
    return (
      <div
        ref={ref}
        {...restProps}
        className={`featured-icon ${className ?? ""}`}
      >
        {isReactElement(Icon) ? <>{Icon}</> : <Icon />}
      </div>
    )
  },
)

FeaturedIcon.displayName = "FeaturedIcon"
